import apiService from 'app/apiService';
import type { APIResponseType } from 'app/apiService/types';
import type { ApiV2FlatFilterModel } from 'app/utils/unifiedSearchFilters';
import {
  buildUnifiedSearchContainsFilterModel,
  mergeApiV2FlatFilterModelsWithAnd,
  stringifyApiV2FlatFilterModel,
} from 'app/utils/unifiedSearchFilters';

export interface DealerSegmentConfigRow {
  primary_segment?: string | null;
  [key: string]: unknown;
}

export interface DealerSegmentSettingsDocument {
  _id?: string;
  type?: string;
  properties?: {
    segments?: DealerSegmentConfigRow[];
  };
}

export type DealerSegmentSettingsResponse =
  | DealerSegmentSettingsDocument
  | Array<{
      settings?: DealerSegmentSettingsDocument[];
    }>;

export interface UnsoldLeadsListMeta {
  totalDocuments?: number;
  totalPages?: number;
  isOpportunity?: boolean;
}

export interface UnsoldLeadsListBody {
  data?: unknown[];
  meta?: UnsoldLeadsListMeta;
  headers?: unknown[];
}

/**
 * Body shape for `GET api/v2/mia/nurturing-journey/stages`.
 *
 * The endpoint now returns a richer `stages: Array<{ code, display_name }>` list
 * that should drive UI labels. The legacy `nurturing_stages: string[]` array is
 * still returned for backward compatibility; consumers should prefer `stages`
 * when present and fall back to `nurturing_stages` otherwise.
 */
export interface NurturingJourneyStageOption {
  code: string;
  display_name?: string;
}

export interface NurturingJourneyStagesApiBody {
  stages?: NurturingJourneyStageOption[];
  nurturing_stages?: unknown[];
  [key: string]: unknown;
}

/** `POST api/v2/nurturing-automation/test?dealer_id=…` — test run from Test Nurturing flow. */
export interface NurturingAutomationTestRequestBody {
  lead_ids: string[];
  nurturing_stage: string;
  /** Optional step-1 selected dealer segment. */
  primary_segment?: string;
}

/** Hardcoded for Test Nurturing unsold-leads list (step 2). */
const UNSOLD_LEADS_MAIQ_LEAD_STATUS = 'AVAILABLE_FOR_NURTURING';

function buildUnsoldLeadsBaseQueryParams(dealerId: string): Record<string, string | boolean> {
  return {
    dealer_id: dealerId,
    mysql: true,
    maiq_lead_status: UNSOLD_LEADS_MAIQ_LEAD_STATUS,
  };
}

/** Default fields for Test Nurturing “search anything” (`contains`, OR between fields). */
export const UNSOLD_UNIFIED_SEARCH_FIELDS = ['name', 'maiq_segment'] as const;

/**
 * Quick-filter / tray segment `eq` rows only. Step‑1 primary segment is sent as
 * the **`segment`** query param on `getUnsoldLeadsPage`, not inside `filters`.
 */
function buildUnsoldLeadsSegmentFilterModel(options: {
  primarySegment: string;
  segmentNames?: string[];
}): ApiV2FlatFilterModel | null {
  const { primarySegment, segmentNames: segmentNamesIn = [] } = options;
  const segmentNames = Array.from(
    new Set(segmentNamesIn.map(s => String(s).trim()).filter(Boolean)),
  );
  const items: { field: string; operator: string; value: string }[] = [];

  const primaryTrim = primarySegment?.trim();
  const namesEffective =
    primaryTrim && segmentNames.length > 1
      ? segmentNames.slice(0, 1)
      : segmentNames;
  for (const n of namesEffective) {
    items.push({ field: 'maiq_segment', operator: 'eq', value: n });
  }

  if (!items.length) return null;
  let logic: 'and' | 'or' = 'and';
  if (namesEffective.length > 1 && !primaryTrim) {
    logic = 'or';
  }
  return { items, logic };
}

/**
 * `filters` query param for unsold leads: optional quick-filter `maiq_segment` (`eq`)
 * values, plus optional unified text search via **`contains`** on `name` and
 * **`maiq_segment`** with **`or`** between those rows (see `app/utils/unifiedSearchFilters.ts`).
 * Step‑1 primary segment is **`segment`** query param, not `filters`.
 */
export function buildUnsoldLeadsCombinedFiltersJson(options: {
  primarySegment: string;
  segmentNames?: string[];
  unifiedSearchText?: string;
  unifiedSearchFields?: readonly string[];
}): string {
  const segment = buildUnsoldLeadsSegmentFilterModel({
    primarySegment: options.primarySegment,
    segmentNames: options.segmentNames,
  });
  const search = buildUnifiedSearchContainsFilterModel(
    options.unifiedSearchText ?? '',
    options.unifiedSearchFields?.length
      ? options.unifiedSearchFields
      : UNSOLD_UNIFIED_SEARCH_FIELDS,
  );
  return stringifyApiV2FlatFilterModel(
    mergeApiV2FlatFilterModelsWithAnd(segment, search),
  );
}

class TestNurturingApiService {
  private static readonly DEALER_SETTINGS_BASE = 'api/v2/dealer-settings';

  private static readonly UNSOLD_LEADS_ENDPOINT = 'api/v2/test-leads';

  private static readonly NURTURING_JOURNEY_STAGES_ENDPOINT =
    'api/v2/mia/nurturing-journey/stages';

  private static readonly NURTURING_AUTOMATION_TEST_ENDPOINT =
    'api/v2/nurturing-automation/test';

  static readonly DEALER_SEGMENTS_CONFIG_TYPE = 'DEALER_SEGMENTS_CONFIG';

  static async getDealerSegmentSettings(
    dealerId: string,
  ): Promise<APIResponseType<DealerSegmentSettingsResponse>> {
    if (!dealerId) {
      return { body: null as any, message: 'Dealer ID is required' };
    }

    return apiService.get<DealerSegmentSettingsResponse>(
      TestNurturingApiService.DEALER_SETTINGS_BASE,
      {
        params: {
          dealer: dealerId,
          type: TestNurturingApiService.DEALER_SEGMENTS_CONFIG_TYPE,
        },
      },
    );
  }

  /** `GET api/v2/mia/nurturing-journey/stages` — no query params (e.g. no `dealer_id`). */
  static async getNurturingJourneyStages(): Promise<
    APIResponseType<NurturingJourneyStagesApiBody>
  > {
    return apiService.get<NurturingJourneyStagesApiBody>(
      TestNurturingApiService.NURTURING_JOURNEY_STAGES_ENDPOINT,
    );
  }

  /**
   * `POST api/v2/nurturing-automation/test?dealer_id=…`
   * Body: `lead_ids`, `nurturing_stage`, optional `primary_segment`.
   */
  static async postNurturingAutomationTest(options: {
    dealerId: string;
    leadIds: string[];
    nurturingStage: string;
    primarySegment?: string;
  }): Promise<APIResponseType<unknown>> {
    const { dealerId, leadIds, nurturingStage, primarySegment = '' } = options;
    const dealerTrim = dealerId?.trim();
    const stageTrim = nurturingStage?.trim();
    const primarySegmentTrim = primarySegment?.trim();
    if (!dealerTrim) {
      throw new Error('Dealer ID is required');
    }
    if (!stageTrim) {
      throw new Error('Nurturing stage is required');
    }
    const ids = leadIds.map(id => String(id).trim()).filter(Boolean);
    if (!ids.length) {
      throw new Error('At least one lead is required');
    }

    const body: NurturingAutomationTestRequestBody = {
      lead_ids: ids,
      nurturing_stage: stageTrim,
    };
    if (primarySegmentTrim) {
      body.primary_segment = primarySegmentTrim;
    }

    return apiService.post<unknown>(
      TestNurturingApiService.NURTURING_AUTOMATION_TEST_ENDPOINT,
      body,
      { params: { dealer_id: dealerTrim } },
      false,
    );
  }

  /**
   * Paginated unsold leads — `GET api/v2/leads/unsold` with `dealer_id`, `mysql`,
   * `maiq_lead_status` (`AVAILABLE_FOR_NURTURING`), `skip` / `limit`, optional `segment`
   * (step‑1 primary), optional `filters`, and optional `sort`.
   */
  static async getUnsoldLeadsPage(options: {
    dealerId: string;
    page: number;
    pageSize: number;
    segment?: string;
    filters?: string;
    sort?: string;
  }): Promise<APIResponseType<UnsoldLeadsListBody>> {
    const { dealerId, page, pageSize, segment = '', filters = '', sort = '' } = options;
    if (!dealerId) {
      return { body: { data: [], meta: { totalDocuments: 0, totalPages: 1 } } };
    }

    const base = buildUnsoldLeadsBaseQueryParams(dealerId);
    const segmentTrimmed = segment.trim();
    const params: Record<string, string | number | boolean> = {
      ...base,
      skip: (page - 1) * pageSize,
      limit: pageSize,
      filters,
      sort,
    };
    if (segmentTrimmed) {
      params.segment = segmentTrimmed;
    }
    return apiService.get<UnsoldLeadsListBody>(TestNurturingApiService.UNSOLD_LEADS_ENDPOINT, {
      params,
    });
  }
}

export default TestNurturingApiService;
